export type TenantClaims = { tenantId: number; tenantCode: string };

export const parseJwtClaims = (token: string): Record<string, string> => {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return {};
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(payload)
        .split('')
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join('')
    );
    const parsed = JSON.parse(json) as Record<string, unknown>;
    return Object.fromEntries(
      Object.entries(parsed).map(([key, value]) => [key, String(value ?? '')])
    );
  } catch {
    return {};
  }
};

export const readTenantFromTokenClaims = (token: string): TenantClaims | null => {
  const claims = parseJwtClaims(token);
  const tenantCode = claims.tenant_code;
  const tenantIdRaw = claims.tenant_id;
  if (!tenantCode || !tenantIdRaw) return null;

  const tenantId = Number.parseInt(tenantIdRaw, 10);
  if (!Number.isFinite(tenantId)) return null;

  return { tenantId, tenantCode };
};

/** Token de cliente scoped al tenant ya resuelto (no el primer key de localStorage). */
export const findStoreTokenForTenant = (tenantCode: string): string | null => {
  if (!tenantCode) return null;
  const scoped = localStorage.getItem(`store_token__${tenantCode}`);
  if (scoped) return scoped;

  const legacy = localStorage.getItem('store_token');
  if (!legacy) return null;

  const claims = readTenantFromTokenClaims(legacy);
  if (claims && claims.tenantCode.toLowerCase() === tenantCode.toLowerCase()) {
    return legacy;
  }
  return null;
};

/**
 * Migra `store_token` legacy (sin sufijo) a la key scoped del tenant resuelto.
 * Solo si el claim del token coincide con ese tenant.
 */
export const migrateLegacyStoreToken = (tenantCode: string): void => {
  if (!tenantCode) return;
  const scopedKey = `store_token__${tenantCode}`;
  if (localStorage.getItem(scopedKey)) {
    localStorage.removeItem('store_token');
    return;
  }

  const legacy = localStorage.getItem('store_token');
  if (!legacy) return;

  const claims = readTenantFromTokenClaims(legacy);
  if (claims && claims.tenantCode.toLowerCase() === tenantCode.toLowerCase()) {
    localStorage.setItem(scopedKey, legacy);
  }
  localStorage.removeItem('store_token');
};

export const findAdminTokenForTenant = (tenantCode: string): string | null => {
  if (!tenantCode) return null;
  return localStorage.getItem(`store_admin_token__${tenantCode}`);
};

export const readTenantFromAdminToken = (token?: string | null): TenantClaims | null => {
  if (!token) return null;
  return readTenantFromTokenClaims(token);
};

/** @deprecated Prefer findStoreTokenForTenant + tenant resuelto por hostname. */
export const findStoreToken = (): string | null => {
  const legacy = localStorage.getItem('store_token');
  if (legacy) return legacy;

  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (key?.startsWith('store_token__')) {
      const value = localStorage.getItem(key);
      if (value) return value;
    }
  }
  return null;
};

/** @deprecated Prefer readTenantFromTokenClaims con el token del tenant resuelto. */
export const readTenantFromToken = (): TenantClaims | null => {
  const token = findStoreToken();
  if (!token) return null;
  return readTenantFromTokenClaims(token);
};
